// Ein Karten-Feature fuer alle Ebenen (ERP/CRM-Plan, Abschnitt 3.2).
//
// GlobalMapDashboard.tsx ist ein imperativer Leaflet-Hotspot (dupliziert sogar
// die Haversine-Formel lokal). Dort wird bewusst NICHT refactort. Stattdessen
// dieser framework-unabhaengige Adapter: er uebersetzt Projekte, Kunden, Leads,
// Freelancer und Ausschreibungen in EINE Form, mit der die neue CRM-Karte
// arbeitet — und die die alte Karte spaeter uebernehmen kann, ohne dass jemand
// sie dafuer anfassen muss.
//
// Pur und ohne Leaflet-Import: die Zuordnung bleibt ohne DOM pruefbar.

/** Welche Ebene das Feature traegt. Bestimmt Farbe und Symbol der Karte. */
export type MapFeatureKind = 'project' | 'customer' | 'lead' | 'freelancer' | 'tender';

export interface MapFeature {
    /** Innerhalb einer Ebene eindeutig; die Karte kombiniert `kind:id`. */
    id: string;
    kind: MapFeatureKind;
    /** Beschriftung des Markers. */
    name: string;
    lat: number;
    lng: number;
    /** Fachlicher Status (Projektstatus, Lead-Stufe, …) — steuert die Farbe. */
    status?: string;
    /** Alles Weitere fuer Popup und Filter; die Karte kennt den Inhalt nicht. */
    meta?: Record<string, unknown>;
}

/**
 * Koordinatenpaar aus beliebigen Rohwerten. null, sobald einer der beiden
 * Werte fehlt oder keine Zahl ist — ein halb verortetes Objekt gehoert nicht
 * auf die Karte, und (0,0) waere der Atlantik.
 */
const toCoords = (lat: unknown, lng: unknown): { lat: number; lng: number } | null => {
    // null/undefined/'' vor Number() abfangen: Number(null) ist 0 und wuerde
    // als gueltige Koordinate durchgehen.
    if (lat == null || lng == null || lat === '' || lng === '') return null;
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
    if (la === 0 && ln === 0) return null;
    return { lat: la, lng: ln };
};

/** Kundenzeile, so wie sie der Typ `Customer` liefert (bewusst schmal gehalten). */
export interface CustomerFeatureLike {
    id: string;
    name: string;
    lat?: number | null;
    lng?: number | null;
    geoQuality?: string | null;
    geoManual?: boolean;
    branche?: string | null;
    city?: string | null;
    zip?: string | null;
    customerNumber?: string | null;
}

/**
 * Kunde -> Karten-Feature. null, wenn der Kunde nicht verortet ist (das ist der
 * Normalfall vor dem ersten Backfill-Lauf und kein Fehler).
 */
export const customerToFeature = (c?: CustomerFeatureLike | null): MapFeature | null => {
    if (!c?.id) return null;
    const coords = toCoords(c.lat, c.lng);
    if (!coords) return null;
    return {
        id: String(c.id),
        kind: 'customer',
        name: String(c.name || 'Unbenannter Kunde'),
        lat: coords.lat,
        lng: coords.lng,
        // Der Kunde hat keinen Lebenszyklus-Status; die Geo-Guete ist die
        // Information, die auf der Karte wirklich zaehlt (ein 'city'-Treffer
        // steht irgendwo im Ortszentrum, nicht am Sitz des Kunden).
        status: c.geoQuality || undefined,
        meta: {
            branche: c.branche || undefined,
            zip: c.zip || undefined,
            city: c.city || undefined,
            customerNumber: c.customerNumber || undefined,
            geoManual: !!c.geoManual,
        },
    };
};

/**
 * Projektzeile, wie sie `loadProjects` liefert (Spalten + `data.metadata`).
 * `lat`/`lng` direkt am Objekt kennt GlobalMapDashboard aus seinem eigenen
 * useMemo — beide Wege werden akzeptiert, damit derselbe Adapter fuer die
 * Rohzeile und fuer die dort bereits aufbereitete Liste passt.
 */
export interface ProjectFeatureLike {
    id?: string;
    name?: string;
    project_number?: string | null;
    status?: string | null;
    user_id?: string | null;
    lat?: number | null;
    lng?: number | null;
    data?: any;
    metadata?: any;
}

/**
 * Projekt -> Karten-Feature. null ohne Koordinaten. Die Koordinaten stehen
 * historisch in `data.metadata.globalData.locationCoordinates` (so liest sie
 * auch nearbyProjects.findNearbyProjects); `metadata` als Kurzform kommt aus
 * der schlanken Projektliste, die nur `data->metadata` selektiert.
 */
export const projectToFeature = (p?: ProjectFeatureLike | null): MapFeature | null => {
    if (!p) return null;
    const meta = p.data?.metadata ?? p.metadata;
    const stored = meta?.globalData?.locationCoordinates;
    const coords = toCoords(p.lat ?? stored?.lat, p.lng ?? stored?.lng);
    if (!coords) return null;
    return {
        id: String(p.id ?? ''),
        kind: 'project',
        name: String(p.name || meta?.projectName || 'Unbenanntes Projekt'),
        lat: coords.lat,
        lng: coords.lng,
        status: p.status || undefined,
        meta: {
            projectNumber: String(p.project_number || meta?.projectNumber || ''),
            address: meta?.projectAddress || undefined,
            coordinator: meta?.coordinator || undefined,
            ownerId: p.user_id || undefined,
        },
    };
};

/** Leadzeile, so wie sie `leadService.Lead` liefert (bewusst schmal gehalten). */
export interface LeadFeatureLike {
    id: string;
    name: string;
    lat?: number | null;
    lng?: number | null;
    /** Pipeline-Stufe (leadLogic.LeadStatus) — steuert die Markerfarbe. */
    status?: string | null;
    branche?: string | null;
    zip?: string | null;
    city?: string | null;
    bundesland?: string | null;
    /** 'scraper:<quelle>' | 'manuell' | … — traegt die Puls-Animation. */
    quelle?: string | null;
    score?: number | null;
    /** ISO-Zeitstempel der Anlage; entscheidet ueber „frischer Fund". */
    createdAt?: string | null;
}

/**
 * Lead -> Karten-Feature. null ohne Koordinaten — ein Lead ohne lat/lng ist
 * der Normalfall vor dem Geocoding-Lauf und kein Fehler.
 *
 * `meta.istScraperFund` steht hier und nicht in der Karte, weil es eine
 * Eigenschaft des Datensatzes ist und keine der Darstellung: die Karte fragt
 * nur noch, ob sie pulsieren laesst.
 */
export const leadToFeature = (l?: LeadFeatureLike | null): MapFeature | null => {
    if (!l?.id) return null;
    const coords = toCoords(l.lat, l.lng);
    if (!coords) return null;
    return {
        id: String(l.id),
        kind: 'lead',
        name: String(l.name || 'Unbenannter Lead'),
        lat: coords.lat,
        lng: coords.lng,
        status: l.status || undefined,
        meta: {
            branche: l.branche || undefined,
            zip: l.zip || undefined,
            city: l.city || undefined,
            bundesland: l.bundesland || undefined,
            quelle: l.quelle || undefined,
            istScraperFund: String(l.quelle || '').startsWith('scraper:'),
            score: typeof l.score === 'number' ? l.score : undefined,
            createdAt: l.createdAt || undefined,
        },
    };
};

/** Ausschreibungszeile aus `tender_opportunities` (snake_case, wie gelesen). */
export interface TenderFeatureLike {
    id: string;
    titel?: string | null;
    auftraggeber?: string | null;
    lat?: number | null;
    lng?: number | null;
    status?: string | null;
    zip?: string | null;
    bundesland?: string | null;
    leistungsort?: string | null;
    /** ISO-Zeitstempel der Abgabefrist — Grundlage des Countdowns. */
    abgabefrist?: string | null;
    dokumenteUrl?: string | null;
    cpvCodes?: string[] | null;
}

/**
 * Ausschreibung -> Karten-Feature.
 *
 * Die Abgabefrist wandert ROH ins Meta und wird hier NICHT in „noch 3 Tage"
 * uebersetzt: eine solche Zahl waere ab dem Moment ihrer Berechnung veraltet,
 * und dieselbe Konvertierung wuerde sie in einem Test einfrieren. Der
 * Countdown entsteht dort, wo gerendert wird (crmMapData.fristTage).
 */
export const tenderToFeature = (t?: TenderFeatureLike | null): MapFeature | null => {
    if (!t?.id) return null;
    const coords = toCoords(t.lat, t.lng);
    if (!coords) return null;
    return {
        id: String(t.id),
        kind: 'tender',
        name: String(t.titel || 'Ausschreibung ohne Titel'),
        lat: coords.lat,
        lng: coords.lng,
        status: t.status || undefined,
        meta: {
            auftraggeber: t.auftraggeber || undefined,
            zip: t.zip || undefined,
            bundesland: t.bundesland || undefined,
            leistungsort: t.leistungsort || undefined,
            abgabefrist: t.abgabefrist || undefined,
            dokumenteUrl: t.dokumenteUrl || undefined,
            cpvCodes: t.cpvCodes?.length ? t.cpvCodes : undefined,
        },
    };
};

/** Freelancerzeile, so wie sie `freelancerLogic.Freelancer` liefert. */
export interface FreelancerFeatureLike {
    id: string;
    name: string;
    firma?: string | null;
    lat?: number | null;
    lng?: number | null;
    /** 'aktiv' | 'pausiert' | 'gesperrt' — steuert die Sichtbarkeit im Layer. */
    status?: string | null;
    /** Radius des Einsatzkreises in km. */
    einsatzradiusKm?: number | null;
    zip?: string | null;
    city?: string | null;
    rating?: number | null;
}

/**
 * Auf wie viele Nachkommastellen die Freelancer-Position gerundet wird.
 *
 * 2 Stellen sind rund 1,1 km in der Breite und in Deutschland rund 0,7 km in
 * der Laenge — ein Raster, in dem eine Wohnadresse nicht mehr identifizierbar
 * ist, das aber fuer „wer wohnt in der Naehe dieser Baustelle" vollauf genuegt.
 */
export const FREELANCER_KOORDINATEN_STELLEN = 2;

/** Auf `stellen` Nachkommastellen runden (halb-auf, wie Math.round). */
const grobeKoordinate = (wert: number, stellen = FREELANCER_KOORDINATEN_STELLEN): number => {
    const faktor = 10 ** stellen;
    return Math.round(wert * faktor) / faktor;
};

/**
 * Freelancer -> Karten-Feature. null ohne Koordinaten.
 *
 * Die Position ist BEWUSST GROB (Plan Abschnitt 6, Codex-Befund 5.3): ein
 * Freelancer ist ein Selbstaendiger, und die hinterlegte Adresse ist in aller
 * Regel seine PRIVATADRESSE. Sie punktgenau auf eine Karte zu setzen, die
 * jeder Innendienstmitarbeiter offen hat, waere eine Preisgabe, fuer die es
 * keinen fachlichen Grund gibt — fuer die Frage „wer ist in der Naehe?"
 * reicht ein Kilometerraster.
 *
 * Gerundet wird auf zwei Dezimalstellen (~1 km). Der Plan nennt als Bild den
 * PLZ-Zentroid; die Rundung wurde ihm vorgezogen, weil `plz_zuordnung.json`
 * ausschliesslich PLZ -> Kreis/Bundesland abbildet und gar keine Koordinaten
 * enthaelt — ein Zentroid muesste erst aus den Kreisgeometrien (739 KB, lazy)
 * berechnet werden und laege bei grossflaechigen Landkreisen weiter vom
 * tatsaechlichen Ort entfernt als das Raster. Die EXAKTEN Koordinaten bleiben
 * in der Datenbank und werden ausschliesslich fuer die Entfernungsrechnung im
 * Matching benutzt (freelancerLogic.suggestFreelancers), die kein Ergebnis auf
 * der Karte hinterlaesst.
 *
 * Der Einsatzradius wandert als Zahl ins Meta; den Kreis zeichnet die Karte.
 */
export const freelancerToFeature = (f?: FreelancerFeatureLike | null): MapFeature | null => {
    if (!f?.id) return null;
    const coords = toCoords(f.lat, f.lng);
    if (!coords) return null;
    return {
        id: String(f.id),
        kind: 'freelancer',
        name: String(f.name || 'Unbenannter Freelancer'),
        lat: grobeKoordinate(coords.lat),
        lng: grobeKoordinate(coords.lng),
        status: f.status || undefined,
        meta: {
            firma: f.firma || undefined,
            einsatzradiusKm:
                typeof f.einsatzradiusKm === 'number' && Number.isFinite(f.einsatzradiusKm)
                    ? f.einsatzradiusKm
                    : undefined,
            // Ort und PLZ sind unkritisch (sie stehen ohnehin im Raster) und
            // machen den Marker im Detail-Panel lesbar.
            zip: f.zip || undefined,
            city: f.city || undefined,
            rating: typeof f.rating === 'number' ? f.rating : undefined,
            // Ehrliches Etikett fuer das Detail-Panel: niemand soll die
            // Markerposition fuer eine Anschrift halten.
            positionGerundet: true,
        },
    };
};

/**
 * Liste konvertieren und die nicht verorteten Eintraege stillschweigend
 * auslassen — der Aufrufer bekommt genau die Marker, die er zeichnen kann.
 */
export const toMapFeatures = <T,>(
    items: T[] | null | undefined,
    convert: (item: T) => MapFeature | null,
): MapFeature[] =>
    (items || []).map(convert).filter((f): f is MapFeature => f !== null);
